import { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  GridItem,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatNumber,
  StatLabel,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { LineChart, PieChart } from '@/components/dashboard'
import {
  userReportsService,
  type UserRegistrationReport,
  type UserEngagementReport,
  type UserDemographicsReport,
} from '@/lib/services/userReportsService'

export default function UserReportsPage() {
  const colors = useThemeColors()
  const [registrationReport, setRegistrationReport] = useState<UserRegistrationReport | null>(null)
  const [engagementReport, setEngagementReport] = useState<UserEngagementReport | null>(null)
  const [demographicsReport, setDemographicsReport] = useState<UserDemographicsReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [registration, engagement, demographics] = await Promise.all([
          userReportsService.getRegistrationReport(),
          userReportsService.getEngagementReport(),
          userReportsService.getDemographicsReport(),
        ])

        setRegistrationReport(registration)
        setEngagementReport(engagement)
        setDemographicsReport(demographics)
      } catch (error) {
        console.error('Error fetching user reports:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchReports()
  }, [])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="auto" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                User Reports
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Detailed reports on user registration, engagement, and behavior patterns
              </Text>
            </CardHeader>

            <CardBody pb={4} flex="1" overflow="auto" minH={0}>
              <Tabs colorScheme="blue">
                <TabList>
                  <Tab>Registration Report</Tab>
                  <Tab>Engagement Report</Tab>
                  <Tab>Demographics Report</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    {registrationReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Users</StatLabel>
                                <StatNumber>{registrationReport.summary.total}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Active Users</StatLabel>
                                <StatNumber>{registrationReport.summary.active}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Inactive Users</StatLabel>
                                <StatNumber>{registrationReport.summary.inactive}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                        </Grid>

                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                          <GridItem>
                            <LineChart
                              title="User Registration Trend"
                              labels={registrationReport.trend.labels}
                              data={registrationReport.trend.data}
                              color="#3182CE"
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <PieChart
                              title="Users by Role"
                              labels={registrationReport.by_role.labels}
                              data={registrationReport.by_role.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <PieChart
                              title="Users by Status"
                              labels={registrationReport.by_status.labels}
                              data={registrationReport.by_status.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                        </Grid>
                      </VStack>
                    )}
                  </TabPanel>

                  <TabPanel>
                    {engagementReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Active (7 days)</StatLabel>
                                <StatNumber>{engagementReport.summary.active_7d}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Active (30 days)</StatLabel>
                                <StatNumber>{engagementReport.summary.active_30d}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Active (90 days)</StatLabel>
                                <StatNumber>{engagementReport.summary.active_90d}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                        </Grid>

                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                          <GridItem>
                            <LineChart
                              title="Activity Timeline"
                              labels={engagementReport.activity_timeline.labels}
                              data={engagementReport.activity_timeline.data}
                              color="#38A169"
                              isLoading={isLoading}
                            />
                          </GridItem>
                        </Grid>

                        <Card>
                          <CardHeader>
                            <Heading size="md">Most Active Users</Heading>
                          </CardHeader>
                          <CardBody>
                            <Table variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>User</Th>
                                  <Th isNumeric>Activity Count</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {engagementReport.active_users.map((user, index) => (
                                  <Tr key={index}>
                                    <Td>{user.user_name}</Td>
                                    <Td isNumeric>{user.activity_count}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </VStack>
                    )}
                  </TabPanel>

                  <TabPanel>
                    {demographicsReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                          <GridItem>
                            <PieChart
                              title="Users by Role"
                              labels={demographicsReport.by_role.labels}
                              data={demographicsReport.by_role.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <PieChart
                              title="Users by Status"
                              labels={demographicsReport.by_status.labels}
                              data={demographicsReport.by_status.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                        </Grid>
                      </VStack>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

